/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import * as THREE from 'three';
import {
  MeshBVH,
  MeshBVHHelper,
  MeshBVHOptions,
  acceleratedRaycast,
  computeBoundsTree,
  disposeBoundsTree,
} from 'three-mesh-bvh';
import { CanvasModel } from '../models/canvas-model';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { ReferenceFrame } from '../models/ReferenceFrame';
import { TerrainGrid, Cell } from '../models/terrain-grid';
import {
  GeoJSONMultiPolygon,
  GeoJSONPolygon,
  GeoJsonObjectType,
  IGeoJSONFeatureCollection,
  IGeoJSONGeometry,
  IGeometryGeoJSON,
} from '../interfaces/geo';
import { DataIndexReference } from '../models/DataIndexReference';
import { Point } from '../generated/models_pb';
import { PaintService } from './paint.service';
import { PaintCell } from '../generated/paint_pb';
import { TerrainDataService } from './terrain-data.service';
import { Store } from '@ngxs/store';
import { PredictorModelState } from 'src/app/state/predictor-model/predictor-model.state';
import { PredictorModelActions } from 'src/app/state/predictor-model/predictor-model.actions';
import { ShapefileDataService } from './shapefile-data.service';
import { InteractionType } from '../enums/interaction-type';
import { CanvasLayer } from 'src/app/global/interfaces/canvas-layer';
import { PaintGroupManager } from './paint-group-manager';
import { PaintGroup } from '../interfaces/paint-group';
import { IndexPointService } from './index-point.service';
import {
  catchError,
  concatMap,
  switchMap,
  takeUntil,
  tap,  
} from 'rxjs/operators';
import { Observable, Subject, from, BehaviorSubject } from 'rxjs';
import { TerrainRenderingStatus } from '../enums/terrain-rendering-status';
import { NotificationsActions } from '../../state/notifications/notifications.actions';
import { Severity } from '../../global/enums/severity';

// Add the extension functions
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

@Injectable({
  providedIn: 'root',
})
export class CanvasService {
  private referenceFrame!: ReferenceFrame;
  private canvasElement!: HTMLCanvasElement;
  private canvasModel!: CanvasModel;
  public agents: number[] = [];
  public paintedCells: number[] = [];
  private fcStore: { [index: number]: IGeoJSONFeatureCollection } = {};
  private cameraMaxFar = 200000;
  private zoomDeltaY = 100;

  public terrainGrid!: TerrainGrid;
  public newVector!: IGeoJSONFeatureCollection;
  width!: number;
  height!: number;
  dataIndexReference: DataIndexReference[] = [];
  textureData!: Uint8Array;
  public debugMode: boolean = false;

  private isAddAgentCompleted = new BehaviorSubject<boolean>(false);
  isCompleted$ = this.isAddAgentCompleted.asObservable();

  setAddAgentCompleted(value: boolean) {
    this.isAddAgentCompleted.next(value);
  }

  constructor(
    private terrainDataService: TerrainDataService,
    private shapefileDataService: ShapefileDataService,
    private paintService: PaintService,
    private store: Store,
    private paintGroupManager: PaintGroupManager,
    private indexPointService: IndexPointService
  ) {
    setTimeout(() => {
      this._animateIfRequested();
    }, 10);

    // Subscribe to ID changes
    this.terrainChange$
      .pipe(
        tap(() => this.resetTerrainData()),
        switchMap(() =>
          this.configTerrainData().pipe(takeUntil(this.stopSubject))
        )
      )
      .pipe(
        catchError((error) => {
          this.store.dispatch(
            new NotificationsActions.Toast(
              'Error building study area terrain.',
              Severity.Error
            )
          );
          throw error;
        })
      )
      .subscribe();
  }

  public onPointerUp(event: MouseEvent) {
    const command: InteractionType = this.store.selectSnapshot(
      PredictorModelState.interactionType
    );
    if (command == InteractionType.None) return;

    event.preventDefault();
    this.mouseMoveBuffer = [];
    switch (command) {
      case InteractionType.Paint: {
        const paintGroup: PaintGroup | null =
          this.paintGroupManager.pointerUpEvent();

        if (!paintGroup) break;

        this.paintCells(paintGroup);
        break;
      }
      case InteractionType.Drop: {
        const injectGroup: PaintGroup | null =
          this.paintGroupManager.pointerUpEvent();
        if (!injectGroup) break;
        this.paintCells(injectGroup);

        break;
      }
      case InteractionType.Erase: {
        this._eraseEventOnMouseUp();
        break;
      }
      default:
        break;
    }
  }

  public onPointerDown(event: MouseEvent) {
    const command: InteractionType = this.store.selectSnapshot(
      PredictorModelState.interactionType
    );
    if (command == InteractionType.None) return;
    event.preventDefault();

    const index: number = this.getCellIndexByMouseEvent(event);
    if (index < 0) {
      return;
    }

    switch (command) {
      case InteractionType.Paint:
        if (this.agentExists(index)) {
          return;
        }
        this.paintCell(index);
        this.addAgent(index);
        this.paintGroupManager.pointerDownEvent(index, true);         
        break;
      case InteractionType.Drop:
        if (this.agentExists(index)) {
          return;
        }
        this.paintCell(index);
        this.addAgent(index);
        this.paintGroupManager.pointerDownEvent(index);       
        break;
      case InteractionType.Erase:
        this._eraseEventOnMouseMove(index);
        break;
      default:
        break;
    }
  }

  public onPointerMove(event: MouseEvent) {
    if (this.canvasModel.controls.enablePan) {
      this.updateTexture();
      this.animate();
    }
    const command: InteractionType = this.store.selectSnapshot(
      PredictorModelState.interactionType
    );
    if (command == InteractionType.None) return;

    event.preventDefault();
    const index: number = this.getCellIndexByMouseEvent(event);
    if (index < 0) {
      return;
    }
    switch (command) {
      case InteractionType.Paint:
      case InteractionType.Drop:
        if (this.agentExists(index)) {
          return;
        }
        this._paintCellsWithoutGaps(index, command);       
        break;
      case InteractionType.Erase:
        this._eraseEventOnMouseMove(index);
        break;
      default:
        break;
    }
  }

  private _prePaintCell(index: number, interactionType: InteractionType) {
    if (interactionType === InteractionType.Drop) {
      if (!this.paintedCells.find((cell) => cell === index)) {
        this.paintCell(index);
      }
      return;
    }
    const neighbours: number[] = this.indexPointService.getNeighboursIndexes(
      index,
      this.terrainGrid.cols,
      this.terrainGrid.rows,
      1
    );
    for (const neighbour of neighbours) {
      if (!this.paintedCells.find((cell) => cell === neighbour)) {
        this.paintCell(neighbour);
      }
    }
  }

  private _paintCellsWithoutGaps(
    index: number,
    interactionType: InteractionType
  ) {
    const connectedPoints: Point.AsObject[] =
      this.indexPointService.connectTwoIndexes(
        this.agents[this.agents.length - 1],
        index,
        this.terrainGrid.cols
      );

    for (const point of connectedPoints) {
      const i: number = this.indexPointService.convertToIndex(
        point,
        this.terrainGrid.cols
      );
      if (this.agentExists(i)) {
        continue;
      }
      this._prePaintCell(i, interactionType);
      this.addAgent(i);
      
      this.paintGroupManager.pointerMoveEvent(i);
    }
  }

  private _clearAgentFromCanvas(index: number) {
    if (this.agentExists(index)) {
      this.removeAgent(index);
      this.removedAgents.push(index);
    }
    this._paintOriginalColor(index);
    this.updateTexture();
    this.animate();    
  }

  private mouseMoveBuffer: number[] = [];
  private _getPreviousLocationOfMouse() {
    if (this.mouseMoveBuffer.length <= 0) return -1;

    return this.mouseMoveBuffer[this.mouseMoveBuffer.length - 1];
  }

  private _eraseEventOnMouseMove(index: number) {
    const connectedPoints: Point.AsObject[] =
      this.indexPointService.connectTwoIndexes(
        this._getPreviousLocationOfMouse(),
        index,
        this.terrainGrid.cols
      );
    this.mouseMoveBuffer.push(index);
    for (const point of connectedPoints) {
      const i: number = this.indexPointService.convertToIndex(
        point,
        this.terrainGrid.cols
      );
      this._deleteNearByAgents(i);
    }
  }

  private _eraseEventOnMouseUp() {
    this.paintService.erase(this.removedAgents, this.terrainGrid);
    this.removedAgents = [];
    console.log(`there are ${this.agents.length} agents on canvas.`);
  }

  private removedAgents: number[] = [];
  private _deleteNearByAgents(index: number) {
    const neighbours: number[] = this.indexPointService.getNeighboursIndexes(
      index,
      this.terrainGrid.cols,
      this.terrainGrid.rows,
      1
    );
    for (const agentIndex of neighbours) {
      this._clearAgentFromCanvas(agentIndex);
    }
  }

  updateTexture() {
    if (this.canvasModel.texture != null) {
      this.canvasModel.texture.needsUpdate = true;
    }
  }

  onWindowResize(indludeSidebar: boolean) {
    let pxWidthSubtraction: number =
      window.innerWidth > 600 ? 90 : 0; /*navbarWidth*/
    if (indludeSidebar) {
      pxWidthSubtraction += 400; /*sideColumnWidth*/
    }
    const pxHeightSubtraction: number =
      72 /*buttonBarHeight*/ +
      (window.innerWidth < 1799
        ? 8
        : 0); /*buttonBarScrollBarHeight(ifPresent)*/

    const width: number = window.innerWidth - pxWidthSubtraction;
    const height: number = window.innerHeight - pxHeightSubtraction;

    // Update the renderer size
    this.canvasModel.renderer.setSize(width, height);

    // Update the camera aspect ratio
    this.canvasModel.camera.aspect = width / height;

    // Update the camera's projection matrix
    this.canvasModel.camera.updateProjectionMatrix();
    this.animate();
  }

  private agentExists(index: number): boolean {
    return this.agents.some((element) => element === index);
  }

  public addAgent(index: number) {
    this.agents.push(index);
  }

  private removeAgent(index: number) {
    const a: number = this.agents.indexOf(index);
    if (a > -1) {
      this.agents.splice(a, 1);
    }
  }

  public eraseAllAgentsFromCanvas() {
    const agentsCopy: number[] = [...this.agents];
    agentsCopy.forEach((agent: number) => {
      this.removeAgent(agent);
    });
    this.paintedCells.forEach((cell) => {
      this._paintOriginalColor(cell);
    });
    this.paintedCells = [];
    if (this.canvasModel.texture != null) {
      this.canvasModel.texture.needsUpdate = true;
    }
    this.animate();
  }

  public clearAllAgents() {
    if (!this.terrainGrid.id) {
      throw Error('Terrain Grid does not have a value for `id` property.');
    }

    this.terrainDataService.clearTerrain(this.terrainGrid.id).subscribe({
      next: () => {
        console.log('cleared terrain');
        this.eraseAllAgentsFromCanvas();        
      },
      error: (error) => {
        console.log(error);
        alert('error. see logs');
      },
    });
  }

  private _paintOriginalColor(index: number) {
    const index3x3: number[] = this.indexPointService.getNeighboursIndexes(
      index,
      this.terrainGrid.cols,
      this.terrainGrid.rows
    );
    for (const i of index3x3) {
      if (this.dataIndexReference[i]) {
        const color: THREE.Color = this.dataIndexReference[i].orgColor!;
        this.textureData[i * 4] = color.r;
        this.textureData[i * 4 + 1] = color.g;
        this.textureData[i * 4 + 2] = color.b;
        this.textureData[i * 4 + 3] = 255;
      }
    }
  }

  private getCellIndexByMouseEvent(event: MouseEvent): number {
    const mouse: THREE.Vector2 = new THREE.Vector2();
    const rect: DOMRect =
      this.canvasModel.renderer.domElement.getBoundingClientRect();

    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.canvasModel.raycaster.setFromCamera(mouse, this.canvasModel.camera);

    const raycaster: THREE.Raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.canvasModel.camera);
    raycaster.firstHitOnly = true;

    const intersects: THREE.Intersection<
      THREE.Object3D<THREE.Object3DEventMap>
    >[] = raycaster.intersectObject(this.canvasModel.mesh, false);

    if (intersects.length <= 0) {
      return -1;
    }

    let index: number = -1;

    if (
      intersects[0].faceIndex !== undefined &&
      intersects[0].faceIndex !== null
    ) {
      const intersect: THREE.Intersection<
        THREE.Object3D<THREE.Object3DEventMap>
      > = intersects[0];
      const uv: THREE.Vector2 = intersect.uv!; // UV coordinates in range [0, 1]

      const material: THREE.Material = this.canvasModel.mesh
        .material as THREE.Material;

      if (material instanceof THREE.MeshBasicMaterial) {
        const texture: THREE.Texture = material.map as THREE.Texture;
        const textureWidth: number = texture.image.width;
        const textureHeight: number = texture.image.height;

        const pixelX: number = Math.floor(uv.x * textureWidth);
        const pixelY: number = Math.floor((1 - uv.y) * textureHeight);

        index = this.calculateIndex(pixelX, pixelY, this.width);
      }
    }

    return index;
  }

  calculateIndex(x: number, y: number, imageWidth: number) {
    return y * imageWidth + x;
  }

  getFeatureCollectionByLayerIndex(
    layerIndex: number
  ): IGeoJSONFeatureCollection {
    return this.fcStore[layerIndex];
  }

  placeAgentsByPointLayer(layerIndex: number): void {
    this.paintService
      .paintCells(this.fcStore[layerIndex], this.terrainGrid)
      .then((response) => {
        response.paintcellsList.forEach((paintCell: PaintCell.AsObject) => {
          if (paintCell.location) {
            const cellIndex: number = this.indexPointService.convertToIndex(
              paintCell.location,
              this.terrainGrid.cols
            );
            if (paintCell.color) {
              this.paintCanvas(
                cellIndex,
                new THREE.Color(
                  paintCell.color.r,
                  paintCell.color.g,
                  paintCell.color.b
                )
              );
            }
            this.addAgent(cellIndex);             
          }
        });
        this.store.dispatch(
          new NotificationsActions.Toast(
            'Agents have been placed successfully.',
            Severity.Success
          )
        );  
        this.updateTexture();
        this.animate();
      });
  }

  placeAgentsByPointLayerOld(shapeFile: CanvasLayer): void {
    const mesh: THREE.InstancedMesh = this.canvasModel.scene.children[
      shapeFile.layerIndex
    ].children[0] as THREE.InstancedMesh;

    const pointPositionArray: THREE.TypedArray =
      mesh.geometry.attributes['position'].array;

    const matrix: THREE.Matrix4 = new THREE.Matrix4();
    const position: THREE.Vector3 = new THREE.Vector3();

    for (let i: number = 0; i < mesh.count; i++) {
      matrix.fromArray(mesh.instanceMatrix.array, i * 16);

      const j: number = i * 3;
      position.set(pointPositionArray[j], pointPositionArray[j + 1], 0);
      position.applyMatrix4(matrix);

      const vector: THREE.Vector3 = position.clone();
      vector.project(this.canvasModel.camera);
      const vector2D: THREE.Vector2 = new THREE.Vector2(vector.x, vector.y);
      this.canvasModel.raycaster.setFromCamera(
        vector2D,
        this.canvasModel.camera
      );
      const intersects: THREE.Intersection<
        THREE.Object3D<THREE.Object3DEventMap>
      >[] = this.canvasModel.raycaster.intersectObject(
        this.canvasModel.mesh,
        false
      );
      let index: number = -1;
      if (intersects[0]?.faceIndex) {
        index = Math.floor(intersects[0].faceIndex / 2);
      }
      if (this.agentExists(index) || index === -1) {
        continue;
      }
      this.paintCell(index);
      this.addAgent(index);
      this.store.dispatch(
        new NotificationsActions.Toast(
          'Agents have been placed successfully.',
          Severity.Success
        )
      ); 
    }
  }

  private inspectedCells: { index: number; color: THREE.Color }[] = [];
  private inspectClearTimeout?: ReturnType<typeof setTimeout>;
  private clearInspectedCells() {
    for (const cell of this.inspectedCells) {
      this.paintCanvas(cell.index, cell.color);
    }
    this.inspectedCells = [];
    this.updateTexture();
    this.animate();
  }

  private paintInspectedCell(index: number) {
    const hasItemWithIndex: boolean = this.inspectedCells.some(
      (cell) => cell.index === index
    );
    this.clearInspectedCells();

    if (hasItemWithIndex) {
      return;
    }
    this.inspectedCells.push({ index, color: this.getCurrentCellColor(index) });
    const color: THREE.Color = new THREE.Color(0, 0, 255);
    this.paintCanvas(index, color);
    this.updateTexture();
    this.animate();
  }

  updateDetails(event: MouseEvent) {
    event.preventDefault();
    if (
      this.store.selectSnapshot(PredictorModelState.interactionType) !==
      InteractionType.None
    ) {
      return;
    }
    const index: number = this.getCellIndexByMouseEvent(event);
    if (index == -1) {
      return;
    }
    if (this.inspectClearTimeout) {
      clearTimeout(this.inspectClearTimeout);
      this.inspectClearTimeout = undefined;
    }

    this.paintInspectedCell(index);

    this.terrainDataService
      .inspectCell(
        this.store.selectSnapshot(PredictorModelState.selectedSimulationId)!,
        index
      )
      .subscribe({
        next: (value) => {
          value.cellIndex = index;
          console.log(
            `Inspect Tool - Cell Index: ${index}, Debris Depth: ${value.debrisDepth}`
          );

          this.store.dispatch(
            new PredictorModelActions.SetInspectDetails(value)
          );
          this.inspectClearTimeout = setTimeout(() => {
            this.clearInspectedCells();
            // Log current cell color from textureData
            const stride: number = index * 4;
            const currentR: number = this.textureData[stride];
            const currentG: number = this.textureData[stride + 1];
            const currentB: number = this.textureData[stride + 2];
            console.log(
              `Current Cell Color - Cell Index: ${index}, RGB: (${currentR}, ${currentG}, ${currentB})`
            );
          }, 1000);
        },
        error: (error) => {
          console.log(error);
          alert('error. see logs');
        },
      });
  }

  canvasConfigured = false;
  addTerrainToCanvas(terrainGrid: TerrainGrid): void {
    this.canvasConfigured = false;
    this.terrainGrid = terrainGrid;

    // kick off the terrain loading process
    this.startProcess(terrainGrid.id || '');
  }

  configureWithoutTerrain(canvas: HTMLCanvasElement): void {
    this.canvasConfigured = false;
    this.canvasElement = canvas;
    this.canvasModel = new CanvasModel();
    this.configScene();
    this.configRenderer(canvas);
    this.configCamera();
    this.configControls(canvas);
    this.configRaycaster();
    this.canvasConfigured = true;
  }

  public clearCanvas() {
    this.removeEntityFromCanvas('study-area');

    if (!this.terrainGrid) return;
    this.canvasModel.renderer.clear();
    this.removeEntityFromCanvas('terrain');
    this.updateTexture();
    this.animate();
  }

  private terrainChange$: Subject<string> = new Subject();
  private stopSubject: Subject<void> = new Subject();

  // Call this method to start the process with a new ID
  startProcess(id: string) {
    this.stopCurrentProcess();
    this.terrainChange$.next(id); // Start a new process
  }

  stopCurrentProcess() {
    this.stopSubject.next(); // Stop any existing process
  }

  configTerrainData(): Observable<void> {
    return from(this.initialSetup()).pipe(
      concatMap(() =>
        this.performStep(
          TerrainRenderingStatus.CreatingTexture,
          this.createTexture.bind(this)
        )
      ),
      concatMap(() =>
        this.performStep(
          TerrainRenderingStatus.CreatingMesh,
          this.createMesh.bind(this)
        )
      ),
      concatMap(() =>
        this.performStep(
          TerrainRenderingStatus.CreatingBVH,
          this.createBVH.bind(this)
        )
      ),
      concatMap(() =>
        this.performStep(
          TerrainRenderingStatus.Painting,
          this.paintTerrain.bind(this)
        )
      ),
      concatMap(() => {
        this.canvasConfigured = true;
        return this.dispatchTerrainState(TerrainRenderingStatus.Rendered);
      })
    );
  }

  private performStep(
    status: TerrainRenderingStatus,
    stepFunction: () => Promise<void>
  ): Observable<void> {
    return new Observable((observer) => {
      stepFunction()
        .then(() => {
          this.dispatchTerrainState(status).then(() => {
            observer.next();
            observer.complete();
          });
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  scaled: boolean = false;

  /** step 1 setup dataIndexes and reference frames */
  private initialSetup(): Promise<void> {
    return new Promise((resolve) => {
      this.width = this.terrainGrid.cols;
      this.height = this.terrainGrid.rows;

      this.dataIndexReference = [];

      for (let row: number = 0; row < this.height; row++) {
        for (let col: number = 0; col < this.width; col++) {
          this.dataIndexReference.push(new DataIndexReference(col, row));
        }
      }

      this.referenceFrame = new ReferenceFrame();
      this.referenceFrame.setFromData(
        Number(this.terrainGrid.lowerleftx),
        Number(this.terrainGrid.lowerlefty),
        Number(this.terrainGrid.cellsize),
        Number(this.width),
        Number(this.height)
      );

      resolve();
    });
  }

  /** step 2 - create texture from terrain grid */
  private async createTexture(): Promise<void> {
    const size: number = this.width * this.height;
    this.textureData = new Uint8Array(4 * size);
    for (let i: number = 0; i < size; i++) {
      const cell: Cell = this.terrainGrid.cellsList[i];
      const color: THREE.Color = new THREE.Color();
      color.setRGB(cell.hillshade, cell.hillshade, cell.hillshade);
      this.dataIndexReference[i].orgColor = color;

      this.paintCanvas(i, color);
    }

    this.canvasModel.texture = new THREE.DataTexture(
      this.textureData,
      this.width,
      this.height
    );
    this.canvasModel.texture.colorSpace = THREE.SRGBColorSpace;
    this.canvasModel.texture.flipY = true;
    this.updateTexture();
  }

  /** step 3 - create the geometry and mesh  */
  private async createMesh(): Promise<void> {
    const geometry: THREE.PlaneGeometry = new THREE.PlaneGeometry(
      Math.abs(
        this.referenceFrame.Extent.MaxX - this.referenceFrame.Extent.MinX
      ),
      Math.abs(
        this.referenceFrame.Extent.MaxY - this.referenceFrame.Extent.MinY
      ),
      20,
      20
    );

    const material: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({
      map: this.canvasModel.texture,
      transparent: false,
      toneMapped: false,
      fog: false,
    });
    this.canvasModel.mesh = new THREE.Mesh(geometry, material);
    this.canvasModel.mesh.name = 'terrain';

    if (this.scaled) this.canvasModel.mesh.scale.set(100, 100, 100);

    const center: THREE.Vector3 = this.getCenterOfTerrain();
    this.canvasModel.mesh.geometry.translate(center.x, center.y, center.z);
  }

  /** step 4 - create the bvh for mesh */
  private async createBVH(): Promise<void> {
    this.canvasModel.mesh.geometry.boundsTree = new MeshBVH(
      this.canvasModel.mesh.geometry,
      {
        maxDepth: 15,
        indirect: true,
      } as MeshBVHOptions
    );

    this.canvasModel.mesh.geometry.computeBoundingBox();

    this.removeEntityFromCanvas('terrain');
    this.canvasModel.scene.add(this.canvasModel.mesh);
  }

  /** step 5 - paint the terrain to the canvas */
  private async paintTerrain(): Promise<void> {
    this.canvasModel.raycaster.params.Mesh = this.canvasModel.mesh;
    this.canvasModel.raycaster.params.Points.threshold = 0.2;

    if (this.debugMode)
      this.canvasModel.scene.add(new MeshBVHHelper(this.canvasModel.mesh, 15));
  }

  private async dispatchTerrainState(
    status: TerrainRenderingStatus
  ): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.store.dispatch(
          new PredictorModelActions.SetTerrainState(
            this.terrainGrid.id!,
            status
          )
        );
        resolve();
      }, 1);
    });
  }

  private resetTerrainData() {
    // Clear data index references
    if (this.dataIndexReference) {
      this.dataIndexReference.length = 0;
    }

    // Clear texture data
    if (this.textureData) {
      this.textureData = new Uint8Array(0);
    }

    // Dispose of texture
    if (this.canvasModel.texture) {
      this.canvasModel.texture.dispose();
      this.canvasModel.texture = new THREE.DataTexture();
    }

    // Remove and dispose of mesh
    if (this.canvasModel.mesh) {
      this.canvasModel.scene.remove(this.canvasModel.mesh);
      this.canvasModel.mesh.geometry.dispose();
      this.canvasModel.mesh = new THREE.Mesh();
    }

    // Clear the scene
    // if (this.canvasModel.scene) {
    //   while (this.canvasModel.scene.children.length > 0) {
    //     this.canvasModel.scene.remove(this.canvasModel.scene.children[0]);
    //   }
    // }

    // Reset width and height
    this.width = 0;
    this.height = 0;
  }

  public paintCanvas(index: number, color: THREE.Color) {
    const stride: number = index * 4;
    this.textureData[stride] = color.r;
    this.textureData[stride + 1] = color.g;
    this.textureData[stride + 2] = color.b;
    this.textureData[stride + 3] = 255;
    this.paintedCells.push(index);
  }

  public getCurrentCellColor(index: number) {
    const color: THREE.Color = new THREE.Color();
    const stride: number = index * 4;
    color.r = this.textureData[stride];
    color.g = this.textureData[stride + 1];
    color.b = this.textureData[stride + 2];
    return color;
  }

  configRaycaster() {
    this.canvasModel.raycaster = new THREE.Raycaster();
  }

  configRenderer(canvas: HTMLCanvasElement) {
    this.canvasModel.renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      antialias: true,
    });
    this.canvasModel.renderer.setPixelRatio(devicePixelRatio);
    this.canvasModel.renderer.setClearColor(0x000000, 0);
    this.canvasModel.renderer.setSize(canvas.clientWidth, canvas.clientHeight);
  }

  configScene() {
    this.canvasModel.scene = new THREE.Scene();
    this.canvasModel.scene.background = new THREE.Color(0x000000);
  }

  configCamera() {
    this.canvasModel.camera = new THREE.PerspectiveCamera(
      60,
      this.canvasModel.renderer.domElement.width /
        this.canvasModel.renderer.domElement.height,
      0.1,
      this.cameraMaxFar
    );
    this.canvasModel.camera.updateMatrixWorld();
  }

  updateCameraFarPlane() {
    this.canvasModel.camera.far = this.cameraMaxFar;
    this.canvasModel.camera.updateProjectionMatrix();
  }

  configControls(canvas: HTMLCanvasElement) {
    this.canvasModel.controls = new OrbitControls(
      this.canvasModel.camera,
      canvas
    );
    this.canvasModel.controls.autoRotate = false;
    this.canvasModel.controls.enableRotate = false;
    this.canvasModel.controls.enableZoom = false;
    this.canvasModel.controls.enablePan = true;
    this.canvasModel.controls.panSpeed = 2;
    this.canvasModel.controls.mouseButtons = { LEFT: THREE.MOUSE.PAN };

    //Locks orbit controls from rotating when panning, preventing map skew
    this.canvasModel.controls.minAzimuthAngle = 0;
    this.canvasModel.controls.maxAzimuthAngle = 0;
    this.canvasModel.controls.minPolarAngle = Math.PI / 2;
    this.canvasModel.controls.maxPolarAngle = Math.PI / 2;

    this.canvasModel.controls.update();
    this.canvasModel.controls.addEventListener('change', () => {
      //Prevents camera from zooming out of visible terrain map range
      if (this.canvasModel.camera.position.z > this.cameraMaxFar) {
        this.canvasModel.camera.position.z = this.cameraMaxFar;
      }

      //Dynamically keeps the terrain map within the pannable area
      if (this.terrainGrid) {
        const terrainWidth: number =
          this.terrainGrid.cols * this.terrainGrid.cellsize;
        const terrainHeight: number =
          this.terrainGrid.rows * this.terrainGrid.cellsize;
        const centerX: number = this.terrainGrid.lowerleftx + terrainWidth / 2;
        const centerY: number = this.terrainGrid.lowerlefty + terrainHeight / 2;
        const panningRangeX: number = terrainWidth / 2;
        const panningRangeY: number = terrainHeight / 2;

        const minX: number = centerX - panningRangeX;
        const maxX: number = centerX + panningRangeX;
        const minY: number = centerY - panningRangeY;
        const maxY: number = centerY + panningRangeY;

        this.canvasModel.controls.target.x = Math.max(
          minX,
          Math.min(maxX, this.canvasModel.controls.target.x)
        );
        this.canvasModel.controls.target.y = Math.max(
          minY,
          Math.min(maxY, this.canvasModel.controls.target.y)
        );
      }
    });
  }

  setCursor(interactionType: InteractionType) {
    switch (interactionType) {
      case InteractionType.Paint:
        this.canvasElement.style.cursor =
          "url('../../../assets/cursors/brush_color-icon_32.png') 0 32, auto";
        break;
      case InteractionType.Drop:
        this.canvasElement.style.cursor =
          "url('../../../assets/cursors/injection-syringe-color-icon-32.png') 0 32, auto";
        break;
      case InteractionType.Erase:
        this.canvasElement.style.cursor =
          "url('../../../assets/cursors/eraser-color-icon-32.png') 12 32, auto";
        break;
      case InteractionType.None:
      default:
        if (this.canvasModel.controls.enablePan) {
          this.canvasElement.style.cursor = 'move';
        } else {
          this.canvasElement.style.cursor = 'default';
        }
        break;
    }
  }

  togglePanning(enablePan?: boolean, interactionUpdate: boolean = false) {
    this.canvasModel.controls.enablePan =
      enablePan ?? !this.canvasModel.controls.enablePan;
    if (this.canvasModel.controls.enablePan || interactionUpdate) {
      this.store.dispatch(
        new PredictorModelActions.SetInteractionType(InteractionType.None)
      );
    }
  }

  zoomIn() {
    if (this.terrainGrid != null) {
      this.adjustCameraOffZoom(-1 * this.zoomDeltaY);
      this.animate();
    }
  }

  zoomOut() {
    if (this.terrainGrid != null) {
      this.adjustCameraOffZoom(this.zoomDeltaY);
      this.animate();
    }
  }

  /**
   * Adjusts the camera zoom based on the delta value.
   * @param deltaY The relative amount to adjust the zoom by.
   */
  adjustCameraOffZoom(deltaY: number) {
    let newZ: number = 10;

    if (this.terrainGrid != null) {
      const zoomFactor: number = 0.8;
      const distanceFactor: number = Math.log(
        this.canvasModel.camera.position.z + 1
      );
      newZ =
        this.canvasModel.camera.position.z +
        deltaY * zoomFactor * distanceFactor;

      if (newZ < 0) {
        newZ = Math.max(10, this.canvasModel.camera.position.z * 0.2);
      }

      if (newZ >= 0 && newZ <= this.cameraMaxFar) {
        this.canvasModel.camera.position.z = newZ;
      }
    }
  }

  handleScrollWheel(event: WheelEvent): void {
    if (this.terrainGrid != null) {
      this.adjustCameraOffZoom(Math.sign(event.deltaY) * this.zoomDeltaY);
      this.animate();
    }
  }

  animate() {
    this.animationRequested = true;
  }

  private _animateIfRequested() {
    if (this.animationRequested && this.canvasModel.renderer != null) {
      this.canvasModel.renderer.render(
        this.canvasModel.scene,
        this.canvasModel.camera
      );
      this.animationRequested = false;
    }
    setTimeout(() => {
      this._animateIfRequested();
    }, 10);
  }

  private animationRequested: boolean = false;

  paintCells(group: PaintGroup) {
    this.paintService
      .paintThemAll(
        group.indexes,
        group.groupId,
        this.terrainGrid,
        group.is3x3 ?? false
      )
      .then((response) => {
        for (const cell in response.paintcellsList) {
          const paintCell: PaintCell.AsObject = response.paintcellsList[cell];
          if (!paintCell.location) {
            throw Error('location is missing');
          }
          if (!paintCell.color) {
            throw Error('color is missing');
          }
          const cellIndex: number = this.indexPointService.convertToIndex(
            paintCell.location,
            this.terrainGrid.cols
          );
          this.paintedCells.push(cellIndex);
          this.terrainGrid.cellsList[cellIndex].color = paintCell.color;

          const color: THREE.Color = new THREE.Color(
            paintCell.color.r,
            paintCell.color.g,
            paintCell.color.b
          );
          this.paintCanvas(cellIndex, color);
        }
        // only update after we have them all.
        // could update this if we painting a lot, then chunk it in the loop if index%100 == 0
        this.updateTexture();
        this.animate();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  paintCell(index: number) {
    //add a color to the screen so the user knows they have drawn while the paint call comes back.
    this.paintCanvas(index, new THREE.Color(128, 128, 128));
    this.updateTexture();
    this.animate();
  }

  removeSimulationPaints(cellIndexes: number[]) {
    for (const cellIndex of cellIndexes) {
      const color: DataIndexReference = this.dataIndexReference[cellIndex];
      if (color && color.orgColor) {
        this.paintCanvas(cellIndex, color.orgColor);
      } else {
        console.log(
          'bad cell',
          cellIndex,
          color,
          this.dataIndexReference.length
        );
      }
    }
    this.updateTexture();
    this.animate();
  }

  private addPolygon(fc: IGeoJSONFeatureCollection) {
    const group: THREE.Group = new THREE.Group();
    for (const feature of fc.features) {
      const jsonGeometry: IGeoJSONGeometry = feature.geometry;
      if (jsonGeometry.type == 'Polygon') {
        const polygon: GeoJSONPolygon =
          jsonGeometry.coordinates as GeoJSONPolygon;
        const polyShape: THREE.Shape | null =
          this.shapefileDataService.polygonToShape(polygon);
        if (polyShape != null) {
          group.add(this.shapefileDataService.polygonShapeToMesh(polyShape));
        }
      } else if (jsonGeometry.type == 'MultiPolygon') {
        const multiPoly: GeoJSONMultiPolygon =
          jsonGeometry.coordinates as GeoJSONMultiPolygon;
        for (const polygon of multiPoly) {
          const polyShape: THREE.Shape | null =
            this.shapefileDataService.polygonToShape(polygon);
          if (polyShape != null) {
            group.add(this.shapefileDataService.polygonShapeToMesh(polyShape));
          }
        }
      }
    }

    return group;
  }

  private addPolyline(fc: IGeoJSONFeatureCollection) {
    const group: THREE.Group = new THREE.Group();
    for (const feature of fc.features) {
      const jsonGeometry: IGeoJSONGeometry = feature.geometry;
      group.add(this.shapefileDataService.polylineShapeToMesh(jsonGeometry));
    }
    return group;
  }

  private addPoint(fc: IGeoJSONFeatureCollection) {
    const group: THREE.Group = new THREE.Group();
    const mesh: THREE.Mesh = this.shapefileDataService.pointsToMesh(
      fc.features
    );
    group.add(mesh);
    return group;
  }

  private focusCanvasOnStudyArea(mesh: THREE.LineSegments) {
    const aabb: THREE.Box3 = new THREE.Box3().setFromObject(mesh);
    const boundingSphere: THREE.Sphere = aabb.getBoundingSphere(
      new THREE.Sphere()
    );
    let diameter: number = boundingSphere.radius * 2;
    //if the diameter is too small then we will just force zoom out.
    if (diameter < 1) diameter = 1;

    this.canvasModel.camera.position.x = boundingSphere.center.x;
    this.canvasModel.camera.position.y = boundingSphere.center.y;
    this.canvasModel.camera.position.z = boundingSphere.center.z + diameter;

    this.cameraMaxFar = this.canvasModel.camera.position.z + this.zoomDeltaY;

    this.canvasModel.camera.lookAt(boundingSphere.center);
    this.canvasModel.controls.target.set(
      boundingSphere.center.x,
      boundingSphere.center.y,
      boundingSphere.center.z
    );

    this.animate();
  }

  public updateStudyAreaOnCanvas(geoJson: IGeometryGeoJSON) {
    if (geoJson.type == 'Polygon') {
      this.clearAllGeoJSON();
      this.canvasModel.renderer.clear();
      this.animate();

      this.renderStudyArea(geoJson, true);
      this.updateCameraFarPlane();
      this.togglePanning(false, true);
    }
  }

  private getCenterOfTerrain() {
    const extent: THREE.Vector3[] = [];
    extent.push(
      new THREE.Vector3(
        this.referenceFrame.Extent.MinX,
        this.referenceFrame.Extent.MaxY
      )
    );
    extent.push(
      new THREE.Vector3(
        this.referenceFrame.Extent.MaxX,
        this.referenceFrame.Extent.MaxY
      )
    );
    extent.push(
      new THREE.Vector3(
        this.referenceFrame.Extent.MaxX,
        this.referenceFrame.Extent.MinY
      )
    );
    extent.push(
      new THREE.Vector3(
        this.referenceFrame.Extent.MinX,
        this.referenceFrame.Extent.MinY
      )
    );
    const aabb: THREE.Box3 = new THREE.Box3();
    aabb.setFromPoints(extent);
    const center: THREE.Vector3 = new THREE.Vector3();
    aabb.getCenter(center);
    return center;
  }

  private renderStudyArea(geoJson: IGeometryGeoJSON, zoomTo: boolean) {
    this.removeEntityFromCanvas('study-area');
    const polygon: GeoJSONPolygon = geoJson.coordinates as GeoJSONPolygon;
    const polyShape: THREE.Shape | null =
      this.shapefileDataService.polygonToShape(polygon);

    if (polyShape != null) {
      const studyAreaLines: THREE.LineSegments<
        THREE.BufferGeometry<THREE.NormalBufferAttributes>,
        THREE.Material | THREE.Material[]
      > = this.shapefileDataService.studyAreaShapeToMesh(polyShape);

      studyAreaLines.name = 'study-area';

      // Set the renderOrder to a higher value to ensure it's rendered on top
      studyAreaLines.renderOrder = 1;

      this.canvasModel.scene.add(studyAreaLines);
      if (zoomTo) {
        this.focusCanvasOnStudyArea(studyAreaLines);
      }
    }
    this.animate();
  }

  private removeEntityFromCanvas(name: string) {
    const selectedObject: THREE.Object3D<THREE.Object3DEventMap> | undefined =
      this.canvasModel.scene.getObjectByName(name);
    if (selectedObject == null) return;

    this.canvasModel.scene.remove(selectedObject);
    this.animate();
  }

  public addGeoJSON(
    fc: IGeoJSONFeatureCollection,
    asScenario: boolean = false
  ): number {
    let group: THREE.Group = new THREE.Group();

    // Check that fc, the first feature, and its geometry are not undefined
    if (fc && fc.features && fc.features[0] && fc.features[0].geometry) {
      const type: GeoJsonObjectType = fc.features[0].geometry.type;
      switch (type) {
        case 'Polygon':
        case 'MultiPolygon':
          group = this.addPolygon(fc);
          break;
        case 'LineString':
          group = this.addPolyline(fc);
          break;
        case 'Point':
          group = this.addPoint(fc);
          break;
      }
    }

    const index: number = asScenario
      ? this.store.selectSnapshot(PredictorModelState.scenarios).length + 2
      : this.store.selectSnapshot(PredictorModelState.shapeFiles).length + 2;

    this.fcStore[index] = fc;

    if (group.children.length > 0) {
      group.layers.set(index);
    }

    this.canvasModel.scene.add(group);
    this.animate();
    return index;
  }

  public setLayerVisibility(layers: CanvasLayer[]): void {
    layers.forEach((layer: CanvasLayer) => {
      this.canvasModel.scene.children[layer.layerIndex].visible = layer.visible;
    });
    this.animate();
  }

  public deleteShapeFile(shapeFile: CanvasLayer) {
    this.canvasModel.scene.remove(
      this.canvasModel.scene.children[shapeFile.layerIndex]
    );

    const currentLayers: CanvasLayer[] = this.store.selectSnapshot(
      PredictorModelState.shapeFiles
    );
    currentLayers.forEach((layer, index) => {
      layer.layerIndex = index + 2;
    });
    this.store.dispatch(
      new PredictorModelActions.UpdateShapeFiles(currentLayers)
    );
    this.animate();
  }

  public clearAllGeoJSON() {
    const childrenCopy: THREE.Object3D<THREE.Object3DEventMap>[] = [
      ...this.canvasModel.scene.children,
    ];
    childrenCopy
      .filter((child) => child instanceof THREE.Group)
      .forEach((group) => {
        this.canvasModel.scene.remove(group);
      });
    this.animate();
  }
}
